/**
 * @fileoverview The canonical band/flow document model (DocumentLayout) shared by the
 * builder, the on-screen preview, and the PDF pipeline.
 * @module document-render/documentLayout
 *
 * Responsibilities: - Define the framework-agnostic document model (pages → bands → elements)
 *                     that the single shared renderer consumes (Phase 1 keystone).
 * Dependencies:     - none (pure types).
 * Architecture:     - Band/flow model (not pixel canvas) so dynamic lists expand and pages
 *                     paginate correctly. Units are points (decision A5).
 * Notes:            - This is the target model; the legacy `documentConfig` is mapped onto it
 *                     by `documentConfigToLayout` (G-COMPAT) so existing templates keep working.
 */

export type DocumentType =
  | 'inspectionReport'
  | 'permit'
  | 'noticeOfViolation'
  | 'letter'
  | 'certificateOfCompliance'
  | 'custom';

/** Structural / content / dynamic band kinds (see 12-document-model-spec). */
export type BandKind =
  | 'letterhead' | 'coverPage' | 'recipientBlock' | 'subjectLine' | 'salutation'
  | 'signatureBlock' | 'legal' | 'footer'
  | 'richText' | 'image' | 'divider' | 'spacer' | 'pageBreak' | 'callout'
  | 'violationsList' | 'photoGallery' | 'metadataGrid' | 'formResponses' | 'advancedFields';

export interface BorderStyle {
  width: number;
  style: 'solid' | 'double' | 'dashed' | 'dotted';
  color: string;
  radius?: number;
}

export interface Spacing { top?: number; right?: number; bottom?: number; left?: number }

export interface PageSetup {
  size: 'letter' | 'a4' | 'legal';
  orientation: 'portrait' | 'landscape';
  margins: Spacing;
  border?: BorderStyle | null;
  watermark?: { text: string; opacity: number; angle: number } | null;
}

export interface ElementStyle {
  fontFamily?: string;
  fontSize?: number;
  color?: string;
  highlight?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: 'left' | 'center' | 'right' | 'justify';
  lineHeight?: number;
}

export interface BandStyle {
  background?: string;
  border?: BorderStyle;
  padding?: Spacing;
  columns?: number;
}

/**
 * A per-slot logo source on a letterhead/cover-page header (G-UX-LOGO-SLOTS).
 * Each of the two header logo slots independently chooses between the department
 * logo on file (resolved from the department theme at render time) or a
 * template-specific uploaded image (an UploadedFile referenced by id).
 */
export interface LogoDescriptor {
  /** 'department' pulls from the department theme; 'uploaded' uses a template-specific file. */
  source: 'department' | 'uploaded';
  /** Set only when source === 'uploaded'; references an UploadedFile by id. */
  uploadedFileId?: string;
  /** When false the slot is omitted entirely from the rendered header. */
  show: boolean;
  /** Which department logo to use when source === 'department'. Defaults to 'primary'.
   *  The document slot label ("primary"/"secondary") is independent of which
   *  department logo it displays — either slot can pick either department logo. */
  departmentLogoSlot?: 'primary' | 'secondary';
}

export type RenderCondition = 'always' | 'hasViolations' | 'noViolations';

export interface BindingDescriptor {
  source: 'violations' | 'photos' | 'metadata' | 'formResponses' | 'advancedField' | 'mergeField';
  fieldId?: string;
  displayMode?: string;
  groupBy?: 'zone' | 'category' | 'severity';
  emptyBehavior?: 'hide' | 'showEmptyMessage';
  criticalField?: boolean;
}

export interface Element {
  id: string;
  type: 'text' | 'image' | 'table' | 'mergeField' | 'field' | 'spacer';
  style?: ElementStyle;
  /** Sanitized rich-text HTML (the only HTML stored). */
  html?: string;
  binding?: BindingDescriptor;
}

export interface Band {
  id: string;
  kind: BandKind;
  enabled: boolean;
  keepTogether?: boolean;
  condition?: RenderCondition;
  style?: BandStyle;
  elements: Element[];
  /** Free-form per-kind configuration carried through from the legacy config. */
  config?: Record<string, unknown>;
  /**
   * Primary logo slot (top-left / centred) on a letterhead/cover-page header (G-UX-LOGO-SLOTS).
   * Only meaningful for `letterhead` and `coverPage` bands.
   */
  primaryLogo?: LogoDescriptor;
  /** Secondary logo slot (top-right) on a letterhead/cover-page header (G-UX-LOGO-SLOTS). */
  secondaryLogo?: LogoDescriptor;
}

/** A first-class cover letter rendered as leading page(s) before the document body. */
export interface CoverLetter {
  /** Independent band array; uses the same Band/Element model as the body. */
  bands: Band[];
}

export interface DocumentLayout {
  schemaVersion: number;
  documentType: DocumentType;
  page: PageSetup;
  themeRef?: string;
  /**
   * Single-author publish lifecycle (Decision D13-R): templates are either a
   * working `draft` or `published`. Only `published` templates are available
   * for document generation. There is no multi-user approval workflow.
   * Defaults to `draft` when absent.
   */
  publishedStatus?: 'draft' | 'published';
  header?: Band | null;
  footer?: Band | null;
  /**
   * Optional cover letter rendered as leading page(s) before the document body.
   * Present when the user has added a cover letter; null/undefined when not.
   */
  coverLetter?: CoverLetter | null;
  body: Band[];
}

export const DOCUMENT_LAYOUT_SCHEMA_VERSION = 1;
